
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiTrash2, FiMail, FiPhone, FiUser, FiCalendar, FiMapPin, FiBriefcase, FiDownload, FiCheckCircle, FiXCircle, FiClock, FiAlertCircle } from 'react-icons/fi';
import AdminNavbar from './AdminNavbar';
import AdminSidebar from './AdminSidebar';
import {
    database,
    ref as dbRef,
    get,
    remove,
    update
} from '../../firebase';

const AdminJobApplications = () => {
    const navigate = useNavigate();
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');

    useEffect(() => {
        // Check authentication
        const isLoggedIn = localStorage.getItem('isLoggedIn');
        if (!isLoggedIn) {
            navigate('/login');
            return;
        }

        loadApplications();
    }, [navigate]);

    const loadApplications = async () => {
        setLoading(true);
        try {
            const appsRef = dbRef(database, 'shivpratapmultistate/applications');
            const snapshot = await get(appsRef);

            if (snapshot.exists()) {
                const data = snapshot.val();
                const appsArray = Object.keys(data).map(key => ({
                    id: key,
                    ...data[key]
                }));

                // Sort by submittedAt desc
                appsArray.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));

                setApplications(appsArray);
            } else {
                setApplications([]);
            }
        } catch (error) {
            console.error('Error loading applications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this application?')) {
            return;
        }

        try {
            const appRef = dbRef(database, `shivpratapmultistate/applications/${id}`);
            await remove(appRef);
            setApplications(prev => prev.filter(item => item.id !== id));
            alert('Application deleted successfully');
        } catch (error) {
            console.error('Error deleting application:', error);
            alert('Error deleting application');
        }
    };

    const handleStatusUpdate = async (id, newStatus) => {
        try {
            const appRef = dbRef(database, `shivpratapmultistate/applications/${id}`);
            await update(appRef, { status: newStatus });

            setApplications(prev => prev.map(item =>
                item.id === id ? { ...item, status: newStatus } : item
            ));
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Error updating status');
        }
    };

    const filteredApplications = applications.filter(app => {
        const matchesSearch = (
            (app.name && app.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (app.email && app.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (app.phone && app.phone.includes(searchTerm))
        );

        const matchesStatus = filterStatus === 'all' || app.status === filterStatus;

        return matchesSearch && matchesStatus;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'reviewed': return 'bg-purple-100 text-purple-800';
            case 'shortlisted': return 'bg-green-100 text-green-800';
            case 'rejected': return 'bg-red-100 text-red-800';
            default: return 'bg-yellow-100 text-yellow-800';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
            <AdminNavbar />
            <AdminSidebar collapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

            <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'} ml-0 pt-16`}>
                <div className="p-4 sm:p-6 lg:p-8">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-800 flex items-center mb-2">
                            <FiBriefcase className="mr-3 text-blue-600" />
                            Job Applications
                        </h1>
                        <p className="text-gray-600">Review applications from candidates</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm p-4 mb-6 border border-gray-200 flex flex-col md:flex-row gap-4 items-center justify-between">
                        <div className="relative w-full md:w-96">
                            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
                            />
                        </div>

                        <div className="flex items-center gap-2 w-full md:w-auto overflow-x-auto">
                            {['all', 'pending', 'reviewed', 'shortlisted', 'rejected'].map(status => (
                                <button
                                    key={status}
                                    onClick={() => setFilterStatus(status)}
                                    className={`px-4 py-2 rounded-lg capitalize whitespace-nowrap transition-colors ${filterStatus === status
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                                        }`}
                                >
                                    {status}
                                </button>
                            ))}
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="text-gray-600 mt-4">Loading applications...</p>
                        </div>
                    ) : filteredApplications.length === 0 ? (
                        <div className="text-center py-16 bg-white rounded-xl border border-gray-200 shadow-sm">
                            <FiAlertCircle className="text-gray-300 text-6xl mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-700">No Applications Found</h3>
                            <p className="text-gray-500 mt-2">No applications match your search or filter.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-6">
                            {filteredApplications.map((app) => (
                                <div key={app.id} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow duration-300">
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-4">
                                            <div>
                                                <div className="flex items-center gap-3 mb-1">
                                                    <h3 className="text-xl font-bold text-gray-800">{app.name}</h3>
                                                    <span className={`text-xs px-2 py-1 rounded-full uppercase font-bold tracking-wider ${getStatusColor(app.status || 'pending')}`}>
                                                        {app.status || 'pending'}
                                                    </span>
                                                </div>
                                                <div className="text-sm text-gray-500 flex items-center gap-2">
                                                    <FiClock size={14} />
                                                    Applied: {new Date(app.submittedAt).toLocaleDateString()}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <select
                                                    value={app.status || 'pending'}
                                                    onChange={(e) => handleStatusUpdate(app.id, e.target.value)}
                                                    className="text-sm border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 p-2 bg-gray-50"
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="reviewed">Reviewed</option>
                                                    <option value="shortlisted">Shortlisted</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                                <button
                                                    onClick={() => handleDelete(app.id)}
                                                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                                    title="Delete Application"
                                                >
                                                    <FiTrash2 size={18} />
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                                                        <FiMail size={14} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase font-semibold">Email</p>
                                                        <a href={`mailto:${app.email}`} className="text-blue-600 hover:underline">{app.email}</a>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600">
                                                        <FiPhone size={14} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase font-semibold">Phone</p>
                                                        <a href={`tel:${app.phone}`} className="text-gray-800 hover:text-blue-600">{app.phone}</a>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600">
                                                        <FiUser size={14} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase font-semibold">Gender / DOB</p>
                                                        <p className="text-gray-800 capitalize">{app.gender} • {app.dateOfBirth}</p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600">
                                                        <FiMapPin size={14} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase font-semibold">Address</p>
                                                        <p className="text-gray-800 line-clamp-1" title={app.address}>{app.address}</p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="space-y-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                                                        <FiBriefcase size={14} />
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase font-semibold">Passing Year</p>
                                                        <p className="text-gray-800">{app.yearOfPassing}</p>
                                                    </div>
                                                </div>

                                                {app.resumeURL ? (
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center text-rose-600">
                                                            <FiDownload size={14} />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 uppercase font-semibold">Resume</p>
                                                            <a
                                                                href={app.resumeURL}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-rose-600 font-medium hover:underline flex items-center gap-1"
                                                            >
                                                                Download / View
                                                            </a>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-3 opacity-50">
                                                        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500">
                                                            <FiXCircle size={14} />
                                                        </div>
                                                        <div>
                                                            <p className="text-xs text-gray-500 uppercase font-semibold">Resume</p>
                                                            <p className="text-gray-800">Not Uploaded</p>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminJobApplications;
